import { type ReactNode, createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { KEYS, readJson, writeJson } from "@/lib/storage";

/** What the user picked in Settings. "system" follows the device setting. */
export type ThemePreference = "system" | "light" | "dark";
export type ColorScheme = "light" | "dark";

interface ThemeState {
  preference: ThemePreference;
  scheme: ColorScheme;
  setPreference: (p: ThemePreference) => void;
}

const DEFAULT_PREFERENCE: ThemePreference = "light";
const PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];
const LIGHT_QUERY = "(prefers-color-scheme: light)";

/** <meta name="theme-color"> per scheme; matches --bg in styles.css. */
const THEME_COLOR: Record<ColorScheme, string> = { dark: "#0B0F0D", light: "#F3F6F4" };

const ThemeContext = createContext<ThemeState | null>(null);

function initialPreference(): ThemePreference {
  const saved = readJson<string>(KEYS.themePreference);
  return PREFERENCES.find((p) => p === saved) ?? DEFAULT_PREFERENCE;
}

function subscribeSystem(onChange: () => void) {
  const mq = window.matchMedia(LIGHT_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

const systemScheme = (): ColorScheme => (window.matchMedia(LIGHT_QUERY).matches ? "light" : "dark");

/**
 * Colors live in styles.css as CSS custom properties, one block per scheme. This
 * provider only decides which block applies by setting <html data-theme>.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(initialPreference);
  const system = useSyncExternalStore(subscribeSystem, systemScheme, () => "light" as const);
  const scheme: ColorScheme = preference === "system" ? system : preference;

  useEffect(() => {
    document.documentElement.dataset.theme = scheme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[scheme]);
  }, [scheme]);

  const value = useMemo<ThemeState>(
    () => ({
      preference,
      scheme,
      setPreference: (p) => {
        setPreferenceState(p);
        writeJson(KEYS.themePreference, p);
      },
    }),
    [preference, scheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
